import { APPLICATION_BREAKPOINTS } from '../styles/utils';
import { useEffect, useState } from 'react';
import { useWindowSize } from './useWindowSize';

/**
 * Application-defined breakpoint keys for responsive design
 * {
 *   "viewportXS": "0px",
 *   "viewportS": "576px",
 *   "viewportM": "768px",
 *   "viewportL": "992px",
 *   "viewportXl": "1200px"
 * }
 */
type keys = keyof typeof APPLICATION_BREAKPOINTS;

/**
 * Custom hook to check if the current window width exceeds a specified breakpoint
 *
 * This hook is particularly useful for implementing responsive design patterns where
 * components need to behave differently based on screen size. It utilizes the application's
 * predefined breakpoints from APPLICATION_BREAKPOINTS.
 *
 * Example usage:
 * // Check if viewport is at least medium size (768px)
 * const isMediumOrLarger = useCheckBreakpoint('viewportM');
 *
 * // Conditional rendering based on breakpoint
 * {isMediumOrLarger ? (
 *   <DesktopLayout />
 * ) : (
 *   <MobileLayout />
 * )}
 *
 * @param breakpoint - The breakpoint key to check against (e.g., 'viewportS', 'viewportM', etc.)
 * @returns boolean - True if current window width is greater than or equal to the specified breakpoint
 */
export function useCheckBreakpoint(viewportSize: keys) {
  const [isGreater, setIsGreater] = useState(false);
  const { width } = useWindowSize();

  const targetViewportSize = APPLICATION_BREAKPOINTS[viewportSize] || 0;

  useEffect(() => {
    if (width && width >= targetViewportSize) {
      setIsGreater(true);
    } else {
      setIsGreater(false);
    }
  }, [width, targetViewportSize]);

  return isGreater;
}
